/**
 * Invoice PDF Export
 *
 * Generates a "factura comercial" style PDF from an invoice object.
 * The invoice should include items[] and (optionally) payments[].
 *
 * @module utils/invoiceExport
 */

import { INVOICE_STATUS_LABELS, INVOICE_PAYMENT_METHOD_LABELS } from './constants';
import { formatCurrency as formatCurrencyShared } from './formatters';

/**
 * Format date for PDF rendering (es-CR, day/month/year)
 * @param {string|Date} value
 * @returns {string}
 */
const formatDate = (value) => {
  if (!value) return '-';
  try {
    const dateObj = typeof value === 'string' ? new Date(value) : value;
    const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    return new Intl.DateTimeFormat('es-CR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(isDateOnly && { timeZone: 'UTC' }),
    }).format(dateObj);
  } catch (err) {
    return '-';
  }
};

/**
 * Format currency for PDF rendering.
 *
 * Adaptador fino sobre la UNICA implementacion (`src/utils/formatters.js`).
 * Antes esto era una copia entera con su propio `currency = 'USD'`: el PDF que
 * ve el cliente tenia su propia mentira, independiente de la del app, asi que
 * arreglar una dejaba la otra intacta. Esa duplicacion es la mitad de #617.
 *
 * Lo unico que agrega sobre la compartida:
 *  - coercion a 0, porque una celda numerica de la factura no puede decir '-';
 *  - `currencyDisplay: 'code'`, porque el `₡` rompe el renglon entero en jsPDF
 *    (ver la nota de encoding en formatters.js). Sale `CRC 2 804 208,00`.
 *
 * NO tiene default de moneda, a proposito. Sin dato, sale el numero sin rotulo.
 *
 * @param {number} amount
 * @param {string} [currency] - ISO 4217 que viene del dato (`invoices.currency`)
 * @returns {string}
 */
const formatCurrency = (amount, currency) =>
  formatCurrencyShared(Number(amount) || 0, currency, 'es-CR', { currencyDisplay: 'code' });

/**
 * #623 — trunca `text` con elipsis para que quepa en `maxWidth` mm, medido
 * con la fuente/tamaño ACTIVOS en `pdfDoc` (llamarla después de fijar el
 * font de la fila, no antes). Devuelve el texto entero si ya entra, y una
 * elipsis sola si ni un carácter entra.
 *
 * Exportada para poder fijar el algoritmo con un `doc` de mentira (sin
 * levantar jsPDF real) además de con el real — ver
 * `invoiceExportUnidadPrecio623.test.js`.
 */
export const truncateToWidth = (pdfDoc, text, maxWidth) => {
  if (maxWidth <= 0) return '';
  if (pdfDoc.getTextWidth(text) <= maxWidth) return text;
  const ellipsis = '…';
  let truncated = text;
  while (truncated.length > 0 && pdfDoc.getTextWidth(`${truncated}${ellipsis}`) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated.length > 0 ? `${truncated}${ellipsis}` : ellipsis;
};

/**
 * #623 — cuánto texto de "Unidad" entra ANTES de chocar con "Precio Unit."
 * de la MISMA fila. `unitPriceText` ya viene formateado (el que se va a
 * dibujar en la celda de al lado) porque el ancho disponible depende de lo
 * ANCHO que sea ESE precio, no de un supuesto fijo — ver el comentario de
 * `cols` en `exportInvoiceToPDF` para la medición que motivó esto.
 *
 * @param {Object} pdfDoc - instancia de jsPDF (o un doble con `getTextWidth`)
 * @param {Object} params
 * @param {string} params.unit
 * @param {string} params.unitPriceText
 * @param {{unit:{x:number,w:number}, unitPrice:{x:number}}} params.cols
 * @param {number} [params.gapMm]
 * @returns {string}
 */
export const computeUnitCellText = (pdfDoc, { unit, unitPriceText, cols, gapMm = 3 }) => {
  const unitPriceLeftEdge = cols.unitPrice.x - pdfDoc.getTextWidth(unitPriceText);
  const maxWidth = Math.min(cols.unit.w, unitPriceLeftEdge - gapMm - cols.unit.x);
  return truncateToWidth(pdfDoc, String(unit || '-'), maxWidth);
};

const COMPANY_INFO = {
  name: 'ProMedic Group',
  address: 'Costa Rica, Santa Ana, Brasil',
  legalId: 'Cedula juridica 3-102-924057',
  phone: '+506 8837-7017',
  email: 'jorge.murillo@promedicgroup.com',
};

/**
 * Export an invoice to a downloadable PDF.
 * @param {Object} invoice - Full invoice including items[] and payments[]
 * @returns {Promise<void>}
 */
export const exportInvoiceToPDF = async (invoice) => {
  if (!invoice) return;

  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;

  let yPos = margin;

  const drawFooter = () => {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(108, 117, 125);
      doc.setFont('helvetica', 'italic');
      doc.text('Gracias por su confianza', pageWidth / 2, pageHeight - 14, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${COMPANY_INFO.email}  |  ${COMPANY_INFO.phone}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.text(
        `Pagina ${i} de ${totalPages}`,
        pageWidth - margin,
        pageHeight - 6,
        { align: 'right' }
      );
    }
  };

  const checkPageBreak = (requiredSpace = 20) => {
    if (yPos + requiredSpace > pageHeight - 25) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // ============ HEADER (Logo placeholder + company info) ============
  doc.setFillColor(13, 110, 253);
  doc.rect(0, 0, pageWidth, 32, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(COMPANY_INFO.name, margin, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(COMPANY_INFO.address, margin, 20);
  doc.text(COMPANY_INFO.legalId, margin, 25);
  doc.text(`${COMPANY_INFO.phone}  |  ${COMPANY_INFO.email}`, margin, 30);

  // Invoice metadata top right
  const status = invoice.status || 'draft';
  const statusLabel = INVOICE_STATUS_LABELS[status] || status;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(`FACTURA #${invoice.invoice_number || ''}`, pageWidth - margin, 14, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Emision: ${formatDate(invoice.invoice_date)}`, pageWidth - margin, 20, { align: 'right' });
  doc.text(`Vencimiento: ${formatDate(invoice.due_date)}`, pageWidth - margin, 25, { align: 'right' });
  doc.text(`Estado: ${statusLabel}`, pageWidth - margin, 30, { align: 'right' });

  yPos = 42;

  // ============ CLIENT + PROJECT INFO ============
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Facturado a:', margin, yPos);

  if (invoice.project) {
    doc.text('Proyecto:', pageWidth / 2 + 5, yPos);
  }
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const client = invoice.project?.client || {};
  const clientLines = [
    client.company_name || '-',
  ];
  if (client.legal_id) clientLines.push(`Cedula: ${client.legal_id}`);
  if (client.primary_contact_email) clientLines.push(client.primary_contact_email);
  if (client.primary_contact_phone) clientLines.push(client.primary_contact_phone);

  let clientYStart = yPos;
  clientLines.forEach((line) => {
    doc.text(String(line), margin, clientYStart);
    clientYStart += 4.5;
  });

  let projectYStart = yPos;
  if (invoice.project) {
    const projectLines = [
      invoice.project.project_number || '-',
      invoice.project.title || '',
    ].filter(Boolean);
    projectLines.forEach((line) => {
      doc.text(String(line), pageWidth / 2 + 5, projectYStart);
      projectYStart += 4.5;
    });
  }

  yPos = Math.max(clientYStart, projectYStart) + 4;

  // ============ ITEMS TABLE ============
  checkPageBreak(20);

  doc.setFillColor(13, 110, 253);
  doc.rect(margin, yPos, contentWidth, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');

  // Column layout — #623: los anchos como % de contentWidth dejaban sólo
  // ~10mm (contentWidth*0.06 de "hueco" + 0.10 de columna) para "Unidad" Y
  // para que "Precio Unit." (crece hacia la IZQUIERDA porque está
  // right-aligned) tuviera lugar. Un precio en colones sin decimal-shrink
  // ("CRC 350 000,00") mide ~23mm solo, así que el texto del precio
  // arrancaba DENTRO de la columna de Unidad y el renderer los dibuja en
  // el orden en que se llaman a `doc.text()`, sin detectar superposición.
  // Medido con `doc.getTextWidth()` sobre el jsPDF real (no a ojo): con la
  // fuente de 9pt que usa esta tabla, "CRC 2 804 208,00" mide 26.13mm y
  // "unidad" 9.43mm — se pisaban por ~7-8mm, exactamente "unidaCRC".
  //
  // El arreglo reserva ~32-38mm en mm FIJOS (no %) entre cada columna
  // right-aligned y su vecina, en vez de porcentajes que no dicen nada
  // sobre cuánto mide el texto real. `unit.w` es sólo el ancho "cómodo"
  // (cubre "cilindro", "rollo de 50m" completos); una unidad más larga que
  // eso se trunca con elipsis — ver `unitMaxWidth` más abajo — CONTRA EL
  // ANCHO REAL del precio de esa fila, no contra un supuesto estático, así
  // que la garantía de "nunca se pisan" no depende de haber adivinado bien
  // el peor caso acá arriba.
  const cols = {
    description: { x: margin + 2, w: 54 },
    type: { x: margin + 76, w: 11 },
    qty: { x: margin + 82, w: 10, align: 'right' },
    unit: { x: margin + 84, w: 18 },
    unitPrice: { x: margin + 134, w: 32, align: 'right' },
    total: { x: margin + contentWidth - 2, w: contentWidth * 0.13, align: 'right' },
  };

  // Espacio mínimo, en mm, entre el final del texto de "Unidad" y el borde
  // izquierdo REAL del texto de "Precio Unit." de esa misma fila.
  const UNIT_PRICE_GAP_MM = 3;

  doc.text('Descripcion', cols.description.x, yPos + 5);
  doc.text('Tipo', cols.type.x, yPos + 5);
  doc.text('Cantidad', cols.qty.x, yPos + 5, { align: 'right' });
  doc.text('Unidad', cols.unit.x, yPos + 5);
  doc.text('Precio Unit.', cols.unitPrice.x, yPos + 5, { align: 'right' });
  doc.text('Total', cols.total.x, yPos + 5, { align: 'right' });

  yPos += 7;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  const items = invoice.items || [];
  // Sin `|| 'USD'` (#617): la moneda sale de `invoices.currency` o no sale.
  // Rellenarla aca era inventar el rotulo de un documento comercial.
  const currency = invoice.currency;

  items.forEach((item, idx) => {
    const description = String(item.description || '-');
    const splitDescription = doc.splitTextToSize(description, cols.description.w - 2);
    const lineHeight = 4.5;
    const rowHeight = Math.max(splitDescription.length * lineHeight + 2, 7);

    checkPageBreak(rowHeight + 5);

    if (idx % 2 === 0) {
      doc.setFillColor(248, 249, 250);
      doc.rect(margin, yPos, contentWidth, rowHeight, 'F');
    }

    const baseY = yPos + 4.5;
    doc.text(splitDescription, cols.description.x, baseY);
    doc.text(String(item.item_type || '-'), cols.type.x, baseY);
    doc.text(String(item.quantity ?? '-'), cols.qty.x, baseY, { align: 'right' });

    // #623 — el precio de ESTA fila decide cuánto lugar le queda a "Unidad".
    // Un precio angosto (USD, montos chicos) deja más margen; uno ancho
    // (CRC de varias cifras) lo come. Medir en vez de asumir es lo que hace
    // que esto no se rompa de nuevo con la próxima unidad larga.
    const unitPriceText = formatCurrency(item.unit_price, currency);
    const unitText = computeUnitCellText(doc, {
      unit: item.unit,
      unitPriceText,
      cols,
      gapMm: UNIT_PRICE_GAP_MM,
    });
    doc.text(unitText, cols.unit.x, baseY);

    doc.text(unitPriceText, cols.unitPrice.x, baseY, { align: 'right' });
    const lineTotal = item.total_price != null ? item.total_price : (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    doc.text(formatCurrency(lineTotal, currency), cols.total.x, baseY, { align: 'right' });

    yPos += rowHeight;
  });

  // Border around table
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);

  yPos += 3;

  // ============ TOTALS ============
  checkPageBreak(35);

  const totalsX = pageWidth - margin - 70;
  const totalsValueX = pageWidth - margin;

  const subtotal = invoice.subtotal != null ? invoice.subtotal : 0;
  const taxRate = invoice.tax_rate != null ? invoice.tax_rate : 0;
  const taxAmount = invoice.tax_amount != null ? invoice.tax_amount : 0;
  const total = invoice.amount != null ? invoice.amount : 0;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', totalsX, yPos);
  doc.text(formatCurrency(subtotal, currency), totalsValueX, yPos, { align: 'right' });
  yPos += 5;

  doc.text(`IVA (${taxRate}%):`, totalsX, yPos);
  doc.text(formatCurrency(taxAmount, currency), totalsValueX, yPos, { align: 'right' });
  yPos += 5;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(totalsX, yPos, totalsValueX, yPos);
  yPos += 4;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAL:', totalsX, yPos);
  doc.text(formatCurrency(total, currency), totalsValueX, yPos, { align: 'right' });
  yPos += 8;

  // ============ PAYMENTS (if any) ============
  const payments = invoice.payments || [];
  if (payments.length > 0) {
    checkPageBreak(30);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text('Historial de Pagos', margin, yPos);
    yPos += 5;

    doc.setFillColor(233, 236, 239);
    doc.rect(margin, yPos, contentWidth, 6, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Fecha', margin + 2, yPos + 4);
    doc.text('Metodo', margin + 35, yPos + 4);
    doc.text('Referencia', margin + 75, yPos + 4);
    doc.text('Monto', pageWidth - margin - 2, yPos + 4, { align: 'right' });
    yPos += 6;

    doc.setFont('helvetica', 'normal');
    let paidTotal = 0;
    payments.forEach((p) => {
      checkPageBreak(8);
      const methodLabel = INVOICE_PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method || '-';
      doc.text(formatDate(p.payment_date), margin + 2, yPos + 4);
      doc.text(String(methodLabel), margin + 35, yPos + 4);
      doc.text(String(p.reference_number || '-'), margin + 75, yPos + 4);
      doc.text(formatCurrency(p.amount, currency), pageWidth - margin - 2, yPos + 4, { align: 'right' });
      paidTotal += Number(p.amount) || 0;
      yPos += 6;
    });

    yPos += 3;
    checkPageBreak(15);

    const balance = invoice.balance != null ? invoice.balance : (total - paidTotal);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Pagado:', totalsX, yPos);
    doc.setTextColor(25, 135, 84);
    doc.text(formatCurrency(paidTotal, currency), totalsValueX, yPos, { align: 'right' });
    yPos += 5;

    doc.setTextColor(0, 0, 0);
    doc.text('Saldo pendiente:', totalsX, yPos);
    doc.setTextColor(220, 53, 69);
    doc.text(formatCurrency(balance, currency), totalsValueX, yPos, { align: 'right' });
    yPos += 8;
    doc.setTextColor(0, 0, 0);
  }

  // ============ NOTES ============
  if (invoice.notes) {
    checkPageBreak(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Notas:', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(String(invoice.notes), contentWidth);
    noteLines.forEach((line) => {
      checkPageBreak(6);
      doc.text(line, margin, yPos);
      yPos += 4.5;
    });
  }

  // Footer for all pages
  drawFooter();

  const filename = `factura-${invoice.invoice_number || 'sin-numero'}.pdf`;
  doc.save(filename);
};

const invoiceExport = {
  exportInvoiceToPDF,
};

export default invoiceExport;
